/**
 * FlatList Component - Unified with Performance Optimization
 *
 * Web version of React Native FlatList with full API compatibility
 * Includes automatic virtualization for large lists
 */

'use client';

import React, {
    forwardRef,
    useImperativeHandle,
    useRef,
    useCallback,
    useState,
    useMemo,
    Fragment,
    type UIEvent,
} from 'react';
import { cn } from '../../utils/cn';
import type { FlatListProps, FlatListMethods, ListRenderItemInfo, ScrollEventData, ViewToken } from './flat-list.types';
import styles from './flat-list.module.css';

// Type definitions for TanStack Virtual (optional dependency)
interface VirtualItem {
    index: number;
    start: number;
    size: number;
    end: number;
    key: string | number;
}

interface Virtualizer {
    getVirtualItems: () => VirtualItem[];
    getTotalSize: () => number;
    scrollToIndex: (index: number, options?: { align?: string; behavior?: string }) => void;
}

type VirtualizerOptions = {
    count: number;
    getScrollElement: () => HTMLElement | null;
    estimateSize: () => number;
    overscan: number;
};

// Optional virtualization support
// Install: yarn add @tanstack/react-virtual
let useVirtualizer: ((options: VirtualizerOptions) => Virtualizer) | null = null;
if (typeof window !== 'undefined') {
    try {
        // eslint-disable-next-line @typescript-eslint/no-require-imports
        const { useVirtualizer: virtualHook } = require('@tanstack/react-virtual');
        useVirtualizer = virtualHook;
    } catch {
        // @tanstack/react-virtual not installed - virtualization disabled
    }
}

/**
 * FlatList Component Implementation with Auto-Optimization
 * Automatically uses TanStack Virtual for lists > threshold (if installed)
 */
function FlatListInner<ItemT>(props: FlatListProps<ItemT>, ref: React.Ref<FlatListMethods<ItemT>>) {
    const {
        data = [],
        renderItem,
        keyExtractor = (item, index) => String(index),
        ItemSeparatorComponent,
        ListHeaderComponent,
        ListFooterComponent,
        ListEmptyComponent,
        horizontal = false,
        inverted = false,
        scrollEnabled = true,
        refreshing = false,
        onRefresh,
        onEndReached,
        onEndReachedThreshold = 0.5,
        onViewableItemsChanged,
        viewabilityConfig,
        onScroll,
        onScrollBeginDrag,
        onScrollEndDrag,
        onMomentumScrollEnd,
        style,
        contentContainerStyle,
        columnWrapperStyle,
        numColumns = 1,
        className,
        contentContainerClassName,
        showsHorizontalScrollIndicator = true,
        showsVerticalScrollIndicator = true,
        stickyHeaderIndices = [],
        debug = false,
        // Performance optimization props
        enableVirtualization = true,
        virtualizationThreshold = 50,
        estimatedItemSize = 200,
        overscan = 3,
        enableInfiniteScroll = true,
        hasMore = false,
    } = props;

    const scrollRef = useRef<HTMLDivElement>(null);
    const [isScrolling, setIsScrolling] = useState(false);
    const [viewableItems, setViewableItems] = useState<ViewToken<ItemT>[]>([]);
    const scrollTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
    const lastScrollY = useRef(0);
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    // Normalize data
    const normalizedData = useMemo(() => {
        if (!data) return [];
        return Array.from(data);
    }, [data]);

    // Decide whether to use virtualization
    const shouldVirtualize = useMemo(() => {
        return (
            enableVirtualization &&
            useVirtualizer !== null &&
            normalizedData.length > virtualizationThreshold &&
            !horizontal && // Virtualization only for vertical lists
            numColumns === 1 // Only for single column
        );
    }, [enableVirtualization, normalizedData.length, virtualizationThreshold, horizontal, numColumns]);

    // Setup virtualizer (if enabled and library available)
    const virtualizer =
        shouldVirtualize && useVirtualizer
            ? // eslint-disable-next-line react-hooks/rules-of-hooks
              useVirtualizer({
                  count: normalizedData.length,
                  getScrollElement: () => scrollRef.current,
                  estimateSize: () => estimatedItemSize,
                  overscan,
              })
            : null;

    // ============================================================================
    // HELPER FUNCTIONS
    // ============================================================================

    const createSeparators = useCallback(
        (index: number) => ({
            highlight: () => {
                if (debug) {
                    // eslint-disable-next-line no-console
                    console.log(`Highlight separator at index ${index}`);
                }
            },
            unhighlight: () => {
                if (debug) {
                    // eslint-disable-next-line no-console
                    console.log(`Unhighlight separator at index ${index}`);
                }
            },
            updateProps: (select: 'leading' | 'trailing', newProps: unknown) => {
                if (debug) {
                    // eslint-disable-next-line no-console
                    console.log(`Update ${select} separator props at index ${index}`, newProps);
                }
            },
        }),
        [debug]
    );

    // ============================================================================
    // SCROLL HANDLERS
    // ============================================================================

    const handleScroll = useCallback(
        (event: UIEvent<HTMLDivElement>) => {
            if (!scrollEnabled) return;

            const target = event.currentTarget;
            const scrollTop = target.scrollTop;
            const scrollLeft = target.scrollLeft;
            const scrollHeight = target.scrollHeight;
            const scrollWidth = target.scrollWidth;
            const clientHeight = target.clientHeight;
            const clientWidth = target.clientWidth;

            const scrollData: ScrollEventData = {
                contentOffset: { x: scrollLeft, y: scrollTop },
                contentSize: { height: scrollHeight, width: scrollWidth },
                layoutMeasurement: { height: clientHeight, width: clientWidth },
            };

            onScroll?.(event, scrollData);

            const isScrollingDown = scrollTop > lastScrollY.current;
            lastScrollY.current = scrollTop;

            // Handle onEndReached for infinite scroll
            if (onEndReached && enableInfiniteScroll && hasMore && !isLoadingMore) {
                const distanceFromEnd = horizontal
                    ? scrollWidth - scrollLeft - clientWidth
                    : scrollHeight - scrollTop - clientHeight;

                const threshold = horizontal
                    ? scrollWidth * onEndReachedThreshold
                    : scrollHeight * onEndReachedThreshold;

                if (distanceFromEnd < threshold && isScrollingDown) {
                    setIsLoadingMore(true);
                    Promise.resolve(onEndReached({ distanceFromEnd })).finally(() => {
                        setIsLoadingMore(false);
                    });
                }
            }

            // Handle viewability
            if (onViewableItemsChanged && viewabilityConfig) {
                const newViewableItems: ViewToken<ItemT>[] = [];

                normalizedData.forEach((item, index) => {
                    const element = scrollRef.current?.querySelector(`[data-index="${index}"]`) as HTMLElement;

                    if (element) {
                        const rect = element.getBoundingClientRect();
                        const containerRect = target.getBoundingClientRect();

                        const isVisible = horizontal
                            ? rect.left >= containerRect.left && rect.right <= containerRect.right
                            : rect.top >= containerRect.top && rect.bottom <= containerRect.bottom;

                        if (isVisible) {
                            newViewableItems.push({
                                item,
                                key: keyExtractor(item, index),
                                index,
                                isViewable: true,
                            });
                        }
                    }
                });

                const hasChanged =
                    newViewableItems.length !== viewableItems.length ||
                    newViewableItems.some((item, i) => item.key !== viewableItems[i]?.key);

                if (hasChanged) {
                    const changed = newViewableItems.filter(item => !viewableItems.find(v => v.key === item.key));
                    setViewableItems(newViewableItems);
                    onViewableItemsChanged({ viewableItems: newViewableItems, changed });
                }
            }

            // Manage scrolling state
            if (!isScrolling) {
                setIsScrolling(true);
                onScrollBeginDrag?.();
            }

            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
            }

            scrollTimeoutRef.current = setTimeout(() => {
                setIsScrolling(false);
                onScrollEndDrag?.();
                onMomentumScrollEnd?.();
            }, 150);
        },
        [
            scrollEnabled,
            onScroll,
            onEndReached,
            onEndReachedThreshold,
            onViewableItemsChanged,
            viewabilityConfig,
            horizontal,
            normalizedData,
            keyExtractor,
            viewableItems,
            isScrolling,
            onScrollBeginDrag,
            onScrollEndDrag,
            onMomentumScrollEnd,
            enableInfiniteScroll,
            hasMore,
            isLoadingMore,
        ]
    );

    // ============================================================================
    // IMPERATIVE METHODS
    // ============================================================================

    useImperativeHandle(
        ref,
        () => ({
            scrollToEnd: params => {
                if (scrollRef.current) {
                    const animated = params?.animated ?? true;
                    const behavior = animated ? 'smooth' : 'auto';

                    if (horizontal) {
                        scrollRef.current.scrollTo({
                            left: scrollRef.current.scrollWidth,
                            behavior,
                        });
                    } else {
                        scrollRef.current.scrollTo({
                            top: scrollRef.current.scrollHeight,
                            behavior,
                        });
                    }
                }
            },
            scrollToIndex: params => {
                if (scrollRef.current) {
                    const { index, animated = true, viewOffset = 0 } = params;
                    const element = scrollRef.current.querySelector(`[data-index="${index}"]`) as HTMLElement;

                    if (element) {
                        const behavior = animated ? 'smooth' : 'auto';
                        const offset = horizontal ? element.offsetLeft - viewOffset : element.offsetTop - viewOffset;

                        if (horizontal) {
                            scrollRef.current.scrollTo({ left: offset, behavior });
                        } else {
                            scrollRef.current.scrollTo({ top: offset, behavior });
                        }
                    }
                }
            },
            scrollToItem: params => {
                const { item, animated = true, viewPosition = 0 } = params;
                const index = normalizedData.findIndex(dataItem => dataItem === item);

                if (index !== -1 && scrollRef.current) {
                    const element = scrollRef.current.querySelector(`[data-index="${index}"]`) as HTMLElement;

                    if (element) {
                        const behavior = animated ? 'smooth' : 'auto';
                        const offset = horizontal
                            ? element.offsetLeft - viewPosition
                            : element.offsetTop - viewPosition;

                        if (horizontal) {
                            scrollRef.current.scrollTo({ left: offset, behavior });
                        } else {
                            scrollRef.current.scrollTo({ top: offset, behavior });
                        }
                    }
                }
            },
            scrollToOffset: params => {
                if (scrollRef.current) {
                    const { offset, animated = true } = params;
                    const behavior = animated ? 'smooth' : 'auto';

                    if (horizontal) {
                        scrollRef.current.scrollTo({ left: offset, behavior });
                    } else {
                        scrollRef.current.scrollTo({ top: offset, behavior });
                    }
                }
            },
            flashScrollIndicators: () => {
                if (scrollRef.current) {
                    scrollRef.current.style.scrollbarColor = 'var(--color-primary-100) transparent';
                    setTimeout(() => {
                        if (scrollRef.current) {
                            scrollRef.current.style.scrollbarColor = '';
                        }
                    }, 500);
                }
            },
            getNativeScrollRef: () => scrollRef.current,
            getScrollableNode: () => scrollRef.current,
            recordInteraction: () => {
                if (debug) {
                    // eslint-disable-next-line no-console
                    console.log('Interaction recorded');
                }
            },
        }),
        [horizontal, normalizedData, debug]
    );

    // ============================================================================
    // RENDER FUNCTIONS
    // ============================================================================

    const renderItemWithSeparator = useCallback(
        (item: ItemT, index: number) => {
            const info: ListRenderItemInfo<ItemT> = {
                item,
                index,
                separators: createSeparators(index),
            };

            const key = keyExtractor(item, index);
            const isSticky = stickyHeaderIndices.includes(index);

            return (
                <Fragment key={key}>
                    <div
                        data-index={index}
                        className={cn(styles.item, isSticky && styles.stickyItem, horizontal && styles.horizontalItem)}
                    >
                        {renderItem(info)}
                    </div>
                    {ItemSeparatorComponent && index < normalizedData.length - 1 && (
                        <div className={styles.separator}>
                            {typeof ItemSeparatorComponent === 'function'
                                ? ItemSeparatorComponent()
                                : ItemSeparatorComponent}
                        </div>
                    )}
                </Fragment>
            );
        },
        [
            renderItem,
            keyExtractor,
            createSeparators,
            ItemSeparatorComponent,
            normalizedData.length,
            stickyHeaderIndices,
            horizontal,
        ]
    );

    const renderColumns = useCallback(() => {
        if (numColumns === 1) {
            return normalizedData.map((item, index) => renderItemWithSeparator(item, index));
        }

        const rows: ItemT[][] = [];
        for (let i = 0; i < normalizedData.length; i += numColumns) {
            rows.push(normalizedData.slice(i, i + numColumns));
        }

        return rows.map((row, rowIndex) => (
            <div key={`row-${rowIndex}`} className={styles.columnWrapper} style={columnWrapperStyle}>
                {row.map((item, colIndex) => {
                    const index = rowIndex * numColumns + colIndex;
                    return renderItemWithSeparator(item, index);
                })}
            </div>
        ));
    }, [normalizedData, numColumns, renderItemWithSeparator, columnWrapperStyle]);

    const renderHeader = useCallback(() => {
        if (!ListHeaderComponent) return null;

        const content = typeof ListHeaderComponent === 'function' ? ListHeaderComponent() : ListHeaderComponent;

        if (!content) return null;

        return <div className={styles.header}>{content}</div>;
    }, [ListHeaderComponent]);

    const renderFooter = useCallback(() => {
        const footerContent =
            ListFooterComponent &&
            (typeof ListFooterComponent === 'function' ? ListFooterComponent() : ListFooterComponent);

        const loadingContent =
            isLoadingMore && enableInfiniteScroll ? (
                <div className={styles.loadingMore}>
                    <div className={styles.spinner} />
                    <span>Loading more...</span>
                </div>
            ) : null;

        if (!footerContent && !loadingContent) return null;

        return (
            <div className={styles.footer}>
                {footerContent}
                {loadingContent}
            </div>
        );
    }, [ListFooterComponent, isLoadingMore, enableInfiniteScroll]);

    const renderEmpty = useCallback(() => {
        if (normalizedData.length > 0 || !ListEmptyComponent) return null;

        return (
            <div className={styles.empty}>
                {typeof ListEmptyComponent === 'function' ? ListEmptyComponent() : ListEmptyComponent}
            </div>
        );
    }, [normalizedData.length, ListEmptyComponent]);

    const renderRefreshControl = useCallback(() => {
        if (!onRefresh) return null;

        return (
            <div className={cn(styles.refreshControl, refreshing && styles.refreshing)}>
                {refreshing && <div className={styles.refreshSpinner} />}
            </div>
        );
    }, [onRefresh, refreshing]);

    // Virtualized render (TanStack Virtual)
    const renderVirtualized = useCallback(() => {
        if (!virtualizer) return null;

        const virtualItems = virtualizer.getVirtualItems();

        return (
            <div
                style={{
                    height: `${virtualizer.getTotalSize()}px`,
                    width: '100%',
                    position: 'relative',
                }}
            >
                {virtualItems.map((virtualItem: VirtualItem) => {
                    const item = normalizedData[virtualItem.index];
                    if (!item) return null;

                    const info: ListRenderItemInfo<ItemT> = {
                        item,
                        index: virtualItem.index,
                        separators: createSeparators(virtualItem.index),
                    };

                    const key = keyExtractor(item, virtualItem.index);

                    return (
                        <div
                            key={key}
                            data-index={virtualItem.index}
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: `${virtualItem.size}px`,
                                transform: `translateY(${virtualItem.start}px)`,
                            }}
                        >
                            {renderItem(info)}
                            {ItemSeparatorComponent && virtualItem.index < normalizedData.length - 1 && (
                                <div className={styles.separator}>
                                    {typeof ItemSeparatorComponent === 'function'
                                        ? ItemSeparatorComponent()
                                        : ItemSeparatorComponent}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    }, [virtualizer, normalizedData, renderItem, keyExtractor, createSeparators, ItemSeparatorComponent]);

    // ============================================================================
    // MAIN RENDER
    // ============================================================================

    const containerClasses = cn(
        styles.container,
        horizontal && styles.horizontal,
        inverted && styles.inverted,
        !scrollEnabled && styles.scrollDisabled,
        !showsHorizontalScrollIndicator && styles.hideHorizontalScrollbar,
        !showsVerticalScrollIndicator && styles.hideVerticalScrollbar,
        shouldVirtualize && styles.virtualized,
        className
    );

    const contentClasses = cn(styles.content, horizontal && styles.horizontalContent, contentContainerClassName);

    return (
        <div
            ref={scrollRef}
            className={containerClasses}
            style={style}
            onScroll={handleScroll}
            role="list"
            aria-label={props['aria-label'] || 'List'}
        >
            <div className={contentClasses} style={contentContainerStyle}>
                {renderRefreshControl()}
                {renderHeader()}
                {renderEmpty()}
                {normalizedData.length > 0 && (shouldVirtualize ? renderVirtualized() : renderColumns())}
                {renderFooter()}
            </div>
        </div>
    );
}

// Export with generic type support
export const FlatList = forwardRef(FlatListInner) as <ItemT>(
    props: FlatListProps<ItemT> & { ref?: React.Ref<FlatListMethods<ItemT>> }
) => React.ReactElement | null;
